import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Card, Container } from "reactstrap";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";

import useGetFormSchema from "custom-hook/useGetFormSchema";
import HeaderCreateItem from "components/Common/HeaderCreateItem";
import { CommonButton } from "components/Common/ButtonCommon";
import httpService from "services/httpService";
import { toastrCRUDSuccess } from "components/Common/AlertToastr";
import { TEXT_POST, TEXT_PUT } from "helpers/name_helper";
import VVSTable from "components/form-control/VVSTable";
import ViewableCommon from "components/Common/ViewableCommon";
import RemoveMemberButton from "components/Common/RemoveMemberButton";

const NewEmployeeGroup = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const schema = yup
    .object({
      name: yup.string().required("This field is required!"),
      branch: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "Branch",
                objectId: val.objectId,
              }
            : null
        )
        .required("This field is required!"),
      leader: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "Employee",
                objectId: val.objectId,
              }
            : null
        )
        .required("This field is required!"),
      department: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "SysCfg",
                objectId: val.objectId,
              }
            : null
        )
        .required("This field is required!"),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors },
    ...rest
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { renderForm } = useGetFormSchema("EmployeeGroup", yup, rest, errors);

  const onSubmit = async values => {
    try {
      const { createdAt, updatedAt, ...employeeGroupValues } = values;
      const employeeGroup = { ...employeeGroupValues };

      if (id) {
        const employeeGroupUrl = `/parse/classes/EmployeeGroup/${id}`;
        await httpService.put(employeeGroupUrl, employeeGroup);
        toastrCRUDSuccess("Employee Group", TEXT_PUT);
        return;
      }

      const employeeGroupUrl = "/parse/classes/EmployeeGroup";
      const res = await httpService.post(employeeGroupUrl, employeeGroup);
      toastrCRUDSuccess("Employee Group", TEXT_POST);
      history.replace(`/employee-group/${res.objectId}`);
    } catch (error) {
      console.log("error", error);
    }
  };

  const getEmployeeGroup = async () => {
    try {
      if (!id) return;

      const { reset } = rest;
      const employeeGroupUrl = `/parse/classes/EmployeeGroup/${id}?include=["branch","department","leader"]`;
      const employeeGroup = await httpService.get(employeeGroupUrl);
      reset({
        ...employeeGroup,
        createdAt: moment(employeeGroup.createdAt).format("YYYY-MM-DD"),
        updatedAt: moment(employeeGroup.updatedAt).format("YYYY-MM-DD"),
        branch: employeeGroup?.branch
          ? {
              text: employeeGroup.branch.name,
              objectId: employeeGroup.branch.objectId,
              className: "Branch",
              __type: "Pointer",
            }
          : { text: "" },
        leader: employeeGroup?.leader
          ? {
              text: employeeGroup.leader.fullName,
              objectId: employeeGroup.leader.objectId,
              className: "Employee",
              __type: "Pointer",
            }
          : { text: "" },
      });
    } catch (error) {
      console.log("error :>> ", error);
    }
  };

  const beforeSaveInlineEdit = row => {
    const updateEmployeeUrl = `/parse/classes/Employee/${row.objectId}`;
    const updateEmployeeBody = {
      group: { __type: "Pointer", className: "EmployeeGroup", objectId: id },
    };
    httpService.put(updateEmployeeUrl, updateEmployeeBody);
  };

  const includeProps = {
    leader: {
      conditionField: {
        position: "leader",
      },
    },
  };

  useEffect(() => {
    getEmployeeGroup();
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <HeaderCreateItem
            title={id ? "Chi tiết nhóm nhân viên" : "Thêm nhóm nhân viên"}
          >
            <CommonButton level={0} type="submit">
              Lưu
            </CommonButton>
          </HeaderCreateItem>
          {renderForm([], includeProps)}
          <ViewableCommon
            if={() => id}
            caseTrue={
              <Card body>
                <VVSTable
                  title="Danh sách thành viên trong nhóm"
                  name="EmployeeGroupMembers"
                  enableInlineEdit
                  disableDelete
                  className="m-0 p-0"
                  beforeSaveInlineEdit={beforeSaveInlineEdit}
                  helperButtons={[
                    {
                      component: RemoveMemberButton,
                      onlyInEditMode: true,
                      componentProps: { removeField: "group" },
                    },
                  ]}
                  whereQuery={{
                    group: {
                      __type: "Pointer",
                      className: "EmployeeGroup",
                      objectId: id,
                    },
                  }}
                />
              </Card>
            }
          />
        </form>
      </Container>
    </div>
  );
};

export default NewEmployeeGroup;
